import type { ReploHostingType } from "@v2/features/onboarding/OnboardingChooseBuilderType";
import type { CreateShopModalProps } from "@v2/types/modalTypes";

import React, { useState } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import {
  useCurrentWorkspace,
  useCurrentWorkspaceId,
} from "@v2/contexts/WorkspaceDashboardContext";
import { useOnboardingCreateProject } from "@v2/features/onboarding/hooks/useOnboardingCreateProject";
import OnboardingChooseBuilderType from "@v2/features/onboarding/OnboardingChooseBuilderType";
import OnboardingShopInfo from "@v2/features/onboarding/OnboardingShopInfo";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import useInstallShopify from "@v2/hooks/useInstallShopify";
import { getProjectLabels } from "@v2/utils/project-labels";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

type ShopInfoModalProps = {
  isMobile: boolean;
  title?: string;
  workspaceId: string | null;
  isShopifyPaymentProcessor: boolean;
  onContinueToSetup: (shopName: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function ShopInfoModal({
  isMobile,
  title,
  workspaceId,
  isShopifyPaymentProcessor,
  onContinueToSetup,
  open,
  onOpenChange,
}: ShopInfoModalProps) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const installShopify = useInstallShopify();
  const { createProject } = useOnboardingCreateProject();
  const projectLabels = getProjectLabels();

  const isBusinessContext =
    user?.profileType === "business" || user?.profileType === "other";

  const [shopName, setShopName] = useState("");
  const [isShopifySelected, setIsShopifySelected] = useState<boolean | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);

  const isFormValid = shopName !== "" && isShopifySelected !== null;

  const handleContinue = async () => {
    if (!isFormValid || !user || !workspaceId) {
      return;
    }

    if (isShopifySelected && !isShopifyPaymentProcessor) {
      onContinueToSetup(shopName);
      return;
    }

    setIsLoading(true);

    const result = await createProject({
      shopName,
      workspaceId,
      hostingType: isShopifyPaymentProcessor ? "shopify" : "replo",
      userEmail: user.email,
    });

    onOpenChange(false);
    if (isShopifyPaymentProcessor && isShopifySelected) {
      installShopify({
        cookie: {
          type: "onboarding",
          workspaceId: result.project.ownerWorkspaceId,
          projectId: result.project.id,
        },
      });
    } else {
      navigate(`/shops/${result.project?.id}/home`);
    }

    setIsLoading(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      onOpenChange(false);
    }
  };

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={handleOpenChange}
        title="Create a new shop"
        description="We'll personalize your setup based on this info."
        footer={
          <Button
            variant="primary"
            size="lg"
            onClick={() => void handleContinue()}
            disabled={isLoading || !isFormValid || !workspaceId}
            loading={isLoading}
            endEnhancer={<ArrowRight />}
            className="w-full"
          >
            Continue
          </Button>
        }
      >
        <OnboardingShopInfo
          shopName={shopName}
          setShopName={setShopName}
          isBusinessContext={isBusinessContext}
          isShopifySelected={isShopifySelected}
          onShopifySelectedChange={setIsShopifySelected}
          isShopifyPaymentProcessor={isShopifyPaymentProcessor}
        />
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title={title ?? `Create a new ${projectLabels.singular}`}
      description="We'll personalize your setup based on this info."
      UNSAFE_className="h-[700px] w-[1100px] max-w-[90vw] p-0 pt-6 sm:max-w-[min(1100px,90vw)] grid-rows-[auto_1fr_auto] [&_[data-slot=modal-title]]:text-center [&_[data-slot=modal-title]]:text-2xl [&_[data-slot=modal-description]]:text-center"
      footer={
        <div className="flex justify-end items-center w-full pt-5 pb-5 md:p-6 border-t-[0.5px] border-border">
          <Button
            variant="primary"
            size="lg"
            onClick={() => void handleContinue()}
            disabled={isLoading || !isFormValid || !workspaceId}
            loading={isLoading}
            endEnhancer={<ArrowRight />}
          >
            Continue
          </Button>
        </div>
      }
    >
      <div className="flex items-start justify-center min-h-0 flex-1 overflow-y-auto py-4 [-ms-overflow-style:none] no-scrollbar">
        <div className="lg:w-[1000px] w-full px-6">
          <OnboardingShopInfo
            shopName={shopName}
            setShopName={setShopName}
            isBusinessContext={isBusinessContext}
            isShopifySelected={isShopifySelected}
            onShopifySelectedChange={setIsShopifySelected}
            isShopifyPaymentProcessor={isShopifyPaymentProcessor}
          />
        </div>
      </div>
    </Modal>
  );
}

type SetupModalProps = {
  isMobile: boolean;
  shopName: string;
  workspaceId: string | null;
  onBack: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function SetupModal({
  isMobile,
  shopName,
  workspaceId,
  onBack,
  open,
  onOpenChange,
}: SetupModalProps) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const installShopify = useInstallShopify();
  const { createProject } = useOnboardingCreateProject();

  const [selectedHostingType, setSelectedHostingType] =
    useState<ReploHostingType | null>(null);
  const [isIntegrateShopifyLoading, setIsIntegrateShopifyLoading] =
    useState(false);
  const [isStartBuildingLoading, setIsStartBuildingLoading] = useState(false);

  const isFormValid = selectedHostingType !== null;

  const handleContinue = async (shouldConnectShopify: boolean = true) => {
    if (!isFormValid || !user || !workspaceId) {
      return;
    }

    if (shouldConnectShopify) {
      setIsIntegrateShopifyLoading(true);
    } else {
      setIsStartBuildingLoading(true);
    }

    const result = await createProject({
      shopName,
      workspaceId,
      hostingType: selectedHostingType,
      userEmail: user.email,
      shouldInvalidateUser: true,
    });

    if (shouldConnectShopify) {
      onOpenChange(false);
      installShopify({
        cookie: {
          type: "onboarding",
          workspaceId: result.project.ownerWorkspaceId,
          projectId: result.project.id,
          hostingType: selectedHostingType,
        },
      });
    } else {
      const redirectPath =
        selectedHostingType === "replo"
          ? `/shops/${result.project.id}/home`
          : `/editor/${result.project.id}/new`;

      onOpenChange(false);
      navigate(redirectPath, { replace: true });
    }

    setIsIntegrateShopifyLoading(false);
    setIsStartBuildingLoading(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      onOpenChange(false);
    }
  };

  const footer = (
    <div className="flex flex-col md:flex-row justify-between items-center gap-2 w-full pt-5 pb-5 md:p-6 border-t-[0.5px] border-border">
      <Button
        variant={isMobile ? "secondary" : "tertiary"}
        size="lg"
        onClick={onBack}
        className="w-full md:w-fit"
      >
        Back
      </Button>
      <div className="flex flex-col md:flex-row items-center gap-2 w-full md:w-auto">
        <Button
          variant="secondary"
          size="lg"
          onClick={() => void handleContinue(false)}
          disabled={
            isIntegrateShopifyLoading || isStartBuildingLoading || !isFormValid
          }
          loading={isStartBuildingLoading}
          className="w-full md:w-fit"
        >
          {isStartBuildingLoading ? "Loading..." : "Start Building First"}
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={() => void handleContinue()}
          disabled={
            isIntegrateShopifyLoading || isStartBuildingLoading || !isFormValid
          }
          loading={isIntegrateShopifyLoading}
          endEnhancer={isIntegrateShopifyLoading ? undefined : <ArrowRight />}
          className="w-full md:w-fit"
        >
          {isIntegrateShopifyLoading ? "Loading..." : "Connect Shopify"}
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={handleOpenChange}
        title="How do you want to use Replo?"
        footer={footer}
      >
        <OnboardingChooseBuilderType
          selectedHostingType={selectedHostingType}
          onSelectHostingType={setSelectedHostingType}
        />
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title="How do you want to use Replo?"
      UNSAFE_className="h-[700px] w-[1100px] max-w-[90vw] p-0 pt-6 sm:max-w-[min(1100px,90vw)] grid-rows-[auto_1fr_auto] [&_[data-slot=modal-title]]:text-center [&_[data-slot=modal-title]]:text-2xl"
      footer={footer}
    >
      <div className="flex items-start justify-center h-full flex-1 overflow-y-auto [-ms-overflow-style:none] no-scrollbar">
        <div className="lg:w-[1000px] w-full px-6">
          <OnboardingChooseBuilderType
            selectedHostingType={selectedHostingType}
            onSelectHostingType={setSelectedHostingType}
          />
        </div>
      </div>
    </Modal>
  );
}

export function CreateShopModal({
  workspaceId: propWorkspaceId,
  title,
  open,
  onOpenChange,
}: CreateShopModalProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const currentWorkspaceId = useCurrentWorkspaceId();
  const workspace = useCurrentWorkspace();
  const isMobile = useIsMobile();

  const [setupState, setSetupState] = useState<{
    showSetup: boolean;
    shopName: string;
  }>({ showSetup: false, shopName: "" });

  const workspaceId = propWorkspaceId ?? currentWorkspaceId;
  const isShopifyPaymentProcessor = workspace?.paymentProcessor === "shopify";

  if (setupState.showSetup) {
    return (
      <SetupModal
        isMobile={isMobile}
        shopName={setupState.shopName}
        workspaceId={workspaceId ?? null}
        onBack={() => setSetupState({ showSetup: false, shopName: "" })}
        open={open}
        onOpenChange={onOpenChange}
      />
    );
  }

  return (
    <ShopInfoModal
      isMobile={isMobile}
      title={title}
      workspaceId={workspaceId ?? null}
      isShopifyPaymentProcessor={isShopifyPaymentProcessor}
      onContinueToSetup={(shopName) => {
        return setSetupState({ showSetup: true, shopName });
      }}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
}
